/**
 * To be used everywhere in the codebase to report performance metrics.
 */
const activePerfReports = new Map<string, ReturnType<typeof beginPerfReport>>()

export const beginPerfReport = (
  label: string,
  opts?: {
    logOnCheckpoint?: boolean
    cpuOnly?: boolean
    onlyReportCheckpointsHigherThanMs?: number
    disable?: boolean
  }
): {
  checkpoint: (label: string) => void
  end: (autoLog?: boolean) => {
    label: string
    total_duration_ms: number
    checkpoints: Record<string, { duration_ms: number }>
  }
} => {
  const useCpuTime = opts?.cpuOnly ?? false

  // Initialize timing based on mode
  const start = useCpuTime ? process.cpuUsage() : performance.now()
  let currentCheckpoint = start
  let status: 'running' | 'ended' = 'running'

  const checkpoints: Record<string, { duration_ms: number }> = {}

  // Helper function to calculate duration
  const calculateDuration = (startTime: any, endTime: any): number => {
    if (useCpuTime) {
      // process.cpuUsage() returns { user: microseconds, system: microseconds }
      const startCpu = startTime as NodeJS.CpuUsage
      const endCpu = endTime as NodeJS.CpuUsage
      // Convert to milliseconds and sum user + system time
      return (
        (endCpu.user + endCpu.system - startCpu.user - startCpu.system) / 1000
      )
    } else {
      // performance.now() returns milliseconds
      return endTime - startTime
    }
  }

  const perfReport = {
    checkpoint: (label: string) => {
      if (opts?.disable) return
      if (status === 'ended') {
        console.warn(
          `Cannot checkpoint a perf report that has already ended: ${label}`
        )
        return
      }
      const end = useCpuTime ? process.cpuUsage() : performance.now()
      const duration = calculateDuration(currentCheckpoint, end)

      // Check if we should include this checkpoint based on duration threshold
      const shouldIncludeCheckpoint =
        opts?.onlyReportCheckpointsHigherThanMs === undefined ||
        duration >= opts.onlyReportCheckpointsHigherThanMs

      if (shouldIncludeCheckpoint) {
        // Accumulate time if checkpoint already exists, otherwise create new one
        if (checkpoints[label]?.duration_ms) {
          checkpoints[label]!.duration_ms += duration
        } else {
          checkpoints[label] = { duration_ms: duration }
        }

        if (opts?.logOnCheckpoint) {
          // eslint-disable-next-line no-console
          console.log(
            `Perf checkpoint for ${label}: ${JSON.stringify(checkpoints[label])}`
          )
        }
      }

      currentCheckpoint = end
    },
    end: (autoLog?: boolean) => {
      if (opts?.disable) return { label, total_duration_ms: 0, checkpoints }
      const end = useCpuTime ? process.cpuUsage() : performance.now()

      // Add remainder checkpoint to capture any untracked time since last checkpoint
      const remainderDuration = calculateDuration(currentCheckpoint, end)
      if (remainderDuration > 0.01) {
        checkpoints['_remainder'] = { duration_ms: remainderDuration }
      }

      activePerfReports.delete(label)
      status = 'ended'
      const totalDuration = calculateDuration(start, end)
      const result = { label, total_duration_ms: totalDuration, checkpoints }
      if (autoLog) {
        // eslint-disable-next-line no-console
        console.log(`Perf report for ${label}: ${JSON.stringify(result)}`)
      }
      return result
    },
  }

  activePerfReports.set(label, perfReport)

  return perfReport
}

export type PerfReport = ReturnType<typeof beginPerfReport>
